/**
 * Configuración → «Marcas de los leads».
 *
 * Los nombres comerciales con los que la clínica recibe contactos (la
 * clínica, un centro asociado, un portal…). Ninguno viene de fábrica: los da
 * de alta un administrador aquí y el servidor los guarda en la base
 * (routers/marcas_router.py). Sin ninguno, la lista de Leads no tiene pestañas
 * de marca e Indicadores no reparte por marca.
 *
 * De cada marca: el nombre (la pestaña y la etiqueta), la clave (sale del
 * nombre y NO se cambia: es lo que llevan guardado los leads), un nombre
 * largo opcional, los alias (palabras que, si aparecen en «de dónde viene»,
 * dicen que es de esta marca), el buzón de Mail que la identifica (quien
 * escribe ahí viene de esta marca) y el color de su etiqueta.
 *
 * Lo ve la gestión (para saber qué hay); solo un administrador lo cambia,
 * como el servidor.
 */
import React, { useEffect, useState } from 'react';
import { api, mensajeError } from '../lib/api';
import { SectionCard } from './Common';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { isAdmin, isGestion } from '../lib/roles';
import { useAuth } from '../lib/auth';
import { Plus, Save, Trash2, Loader2, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import MarcaLead from './MarcaLead';

const VACIA = { nombre: '', nombre_largo: '', alias: '', buzon: '', color: '' };

/** Los alias como se teclean: separados por comas. */
const aliasEnTexto = (alias) => (alias || []).join(', ');
const aliasDeTexto = (texto) => String(texto || '').split(',').map((a) => a.trim()).filter(Boolean);

function Formulario({ valor, onChange, buzones, idPrefijo, conColor = true }) {
  const set = (k, v) => onChange({ ...valor, [k]: v });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div>
        <Label htmlFor={`${idPrefijo}-nombre`}>Nombre</Label>
        <Input id={`${idPrefijo}-nombre`} value={valor.nombre} maxLength={60}
          data-testid={`${idPrefijo}-nombre`} onChange={(e) => set('nombre', e.target.value)} />
      </div>
      <div>
        <Label htmlFor={`${idPrefijo}-nombre-largo`}>Nombre largo (opcional)</Label>
        <Input id={`${idPrefijo}-nombre-largo`} value={valor.nombre_largo} maxLength={120}
          data-testid={`${idPrefijo}-nombre-largo`} onChange={(e) => set('nombre_largo', e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <Label htmlFor={`${idPrefijo}-alias`}>Alias en «de dónde viene» (separados por comas)</Label>
        <Input id={`${idPrefijo}-alias`} value={valor.alias} placeholder="p. ej. nombre corto, su web"
          data-testid={`${idPrefijo}-alias`} onChange={(e) => set('alias', e.target.value)} />
      </div>
      <div>
        <Label htmlFor={`${idPrefijo}-buzon`}>Buzón de Mail (opcional)</Label>
        <select id={`${idPrefijo}-buzon`} value={valor.buzon} data-testid={`${idPrefijo}-buzon`}
          onChange={(e) => set('buzon', e.target.value)}
          className="mt-1 block w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">Ninguno</option>
          {buzones.map((b) => <option key={b.clave} value={b.clave}>{b.etiqueta}</option>)}
        </select>
      </div>
      {conColor ? (
        <div>
          <Label htmlFor={`${idPrefijo}-color`}>Color (opcional, #RRGGBB)</Label>
          <Input id={`${idPrefijo}-color`} value={valor.color} maxLength={7} placeholder="#2E6E5E"
            data-testid={`${idPrefijo}-color`} onChange={(e) => set('color', e.target.value.trim())} />
        </div>
      ) : null}
    </div>
  );
}

export default function AjustesMarcas() {
  const { user } = useAuth();
  const puedeVer = isGestion(user);
  const puedeCambiar = isAdmin(user);
  const [marcas, setMarcas] = useState(null);
  const [buzones, setBuzones] = useState([]);
  const [fallo, setFallo] = useState('');
  const [nueva, setNueva] = useState(VACIA);
  const [editando, setEditando] = useState(null);   // {id, ...campos}
  const [ocupado, setOcupado] = useState(false);

  const cargar = async () => {
    try {
      const r = await api.get('/api/marcas');
      setMarcas(r.data?.marcas || []);
      setBuzones(r.data?.buzones || []);
      setFallo('');
    } catch (e) {
      // Un fallo no es «no hay marcas»: se dice.
      setFallo(mensajeError(e, 'No se han podido leer las marcas.'));
    }
  };
  useEffect(() => { if (puedeVer) cargar(); }, [puedeVer]);

  if (!puedeVer) return null;

  const cuerpo = (v) => ({
    nombre: v.nombre.trim(),
    nombre_largo: v.nombre_largo.trim(),
    alias: aliasDeTexto(v.alias),
    buzon: v.buzon,
    color: v.color.trim(),
  });

  const crear = async () => {
    if (ocupado || !nueva.nombre.trim()) return;
    setOcupado(true);
    try {
      await api.post('/api/marcas', cuerpo(nueva));
      toast.success(`Marca «${nueva.nombre.trim()}» dada de alta`);
      setNueva(VACIA);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido dar de alta la marca.'));
    } finally { setOcupado(false); }
  };

  const guardar = async () => {
    if (ocupado || !editando) return;
    setOcupado(true);
    try {
      await api.put(`/api/marcas/${editando.id}`, cuerpo(editando));
      toast.success('Marca guardada');
      setEditando(null);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido guardar la marca.'));
    } finally { setOcupado(false); }
  };

  const borrar = async (m) => {
    if (!window.confirm(`¿Borrar la marca «${m.nombre}»?\n\nLos leads no se tocan: los que la tenían puesta a mano vuelven a lo que digan su correo y «de dónde viene».`)) return;
    setOcupado(true);
    try {
      await api.delete(`/api/marcas/${m.id}`);
      toast.success('Marca borrada');
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido borrar la marca.'));
    } finally { setOcupado(false); }
  };

  const etiquetaBuzon = (clave) => buzones.find((b) => b.clave === clave)?.etiqueta || clave;

  return (
    <SectionCard title="Marcas de los leads" className="lg:col-span-2">
      <div data-testid="ajustes-marcas">
        <p className="text-sm text-muted-foreground mb-3">
          Los nombres con los que la clínica recibe contactos. Cada lead se reparte en su
          marca por el buzón de Mail al que escribió o por lo que diga «de dónde viene»; si no
          hay ninguna, la lista de Leads no enseña pestañas de marca.
          {puedeCambiar ? '' : ' Solo un administrador puede cambiarlas.'}
        </p>
        {fallo ? (
          <p className="text-sm text-red-700" data-testid="ajustes-marcas-fallo">{fallo}</p>
        ) : marcas === null ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : marcas.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="ajustes-marcas-vacio">
            Todavía no hay ninguna marca dada de alta.
          </p>
        ) : (
          <ul className="divide-y border rounded-md" data-testid="ajustes-marcas-lista">
            {marcas.map((m) => (
              <li key={m.id} className="p-3" data-testid={`ajustes-marca-${m.clave}`}>
                {editando?.id === m.id ? (
                  <div className="space-y-3">
                    <Formulario valor={editando} onChange={setEditando} buzones={buzones}
                      idPrefijo="ajustes-marca-editar" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={guardar} disabled={ocupado || !editando.nombre.trim()}
                        data-testid="ajustes-marca-guardar">
                        {ocupado ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        Guardar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditando(null)}>
                        <X className="w-4 h-4 mr-1" /> Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <MarcaLead marca={m.clave} marcas={marcas} />
                    <span className="text-xs text-muted-foreground">
                      clave «{m.clave}»
                      {m.nombre_largo ? ` · ${m.nombre_largo}` : ''}
                      {m.buzon ? ` · buzón: ${etiquetaBuzon(m.buzon)}` : ''}
                      {(m.alias || []).length ? ` · alias: ${aliasEnTexto(m.alias)}` : ''}
                    </span>
                    {puedeCambiar ? (
                      <span className="ml-auto flex gap-1">
                        <Button size="sm" variant="ghost" title="Cambiar" data-testid={`ajustes-marca-editar-${m.clave}`}
                          onClick={() => setEditando({
                            id: m.id, nombre: m.nombre || '', nombre_largo: m.nombre_largo || '',
                            alias: aliasEnTexto(m.alias), buzon: m.buzon || '', color: m.color || '',
                          })}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" title="Borrar" disabled={ocupado}
                          data-testid={`ajustes-marca-borrar-${m.clave}`} onClick={() => borrar(m)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </span>
                    ) : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        {puedeCambiar && !fallo ? (
          <div className="mt-4 space-y-3 rounded-md border border-dashed p-3" data-testid="ajustes-marca-nueva">
            <p className="text-sm font-medium">Nueva marca</p>
            <Formulario valor={nueva} onChange={setNueva} buzones={buzones} idPrefijo="ajustes-marca-nueva" />
            <Button size="sm" onClick={crear} disabled={ocupado || !nueva.nombre.trim()}
              data-testid="ajustes-marca-crear">
              {ocupado ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
              Dar de alta
            </Button>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
}
